import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "./../notification.hook";
import { RefreshReactQuery } from "../../constants/AppConstants";
import {
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../../constants/Dictionary";
import CmSheetSoftSkillsService from "../../services/api/cmSheet/CmSheetSoftSkills.service";
import { UpdateCollaboratorCmSheetSoftSkillsRequest } from "../../types/api/collaborators.type";
import { useEffect } from "react";
import { CM_SHEET_STATE_MACHINE_QUERY_KEY } from "./stateMachine.hook";

const CM_SHEET_SOFT_SKILLS_QUERY_KEY = "get-cm-sheet-soft-skills";

export const useCmSheetSoftSkills = (cmSheetId: number, enabled = true) => {
  const { notification } = useNotification();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [CM_SHEET_SOFT_SKILLS_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetSoftSkillsService.get(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
    enabled: enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useUpdateCmSheetSoftSkills = (cmSheetId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: updateSoftSkills, isPending: isUpdating } = useMutation({
    mutationFn: (body: UpdateCollaboratorCmSheetSoftSkillsRequest) =>
      CmSheetSoftSkillsService.update(cmSheetId, body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [CM_SHEET_SOFT_SKILLS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY],
        }),
      ]);
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { updateSoftSkills, isUpdating };
};
